import { createNDJSONStreamWorker } from '../workers/ndjsonStream.worker';
import { getApiBaseUrl, getAuthToken } from './http';

export type SqlStreamOptions = {
  table: string;
  columns?: string[];
  orderBy?: string[] | string;
  batchSize?: number;
  flushMs?: number;
  database?: string | null;
  headers?: Record<string, string>;
  onBatch?: (rows: any[]) => void;
  onDone?: () => void;
  onError?: (error: any) => void;
  signal?: AbortSignal;
};

export type SqlStreamController = {
  abort: () => void;
};

export const BATCH_SIZE = 20000;
export const FLUSH_MS = 200;


export const fetchSqlDataStreaming = (opts: SqlStreamOptions & { flowControl?: boolean }): SqlStreamController & { ready?: () => void } => {
  const base = getApiBaseUrl();
  const params = new URLSearchParams();
  params.set('table', String(opts.table));
  if (Array.isArray(opts.columns) && opts.columns.length) {
    params.set('columns', opts.columns.join(','));
  }
  if (typeof opts.orderBy === 'string' && opts.orderBy.trim()) {
    params.set('order_by', opts.orderBy);
  } else if (Array.isArray(opts.orderBy) && opts.orderBy.length) {
    params.set('order_by', opts.orderBy.join(','));
  }
  const url = `${base}/dbExchange/stream?${params.toString()}`;
  const token = getAuthToken();
  const headers: Record<string, string> = {
    Accept: 'application/x-ndjson',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(opts.headers || {}),
  };
  const worker = createNDJSONStreamWorker();
  const cleanup = () => {
    try { worker.terminate(); } catch {}
  };
  worker.onmessage = (evt: MessageEvent<any>) => {
    const msg = evt.data || {};
    if (msg.type === 'batch' && Array.isArray(msg.rows)) {
      opts.onBatch?.(msg.rows);
    } else if (msg.type === 'done') {
      opts.onDone?.();
      cleanup();
    } else if (msg.type === 'error') {
      opts.onError?.(msg.error || new Error('stream error'));
      cleanup();
    } else if (msg.type === 'aborted') {
      cleanup();
    }
  };
  worker.postMessage({
    type: 'start',
    url,
    headers,
    batchSize: typeof opts.batchSize === 'number' ? opts.batchSize : BATCH_SIZE,
    flushMs: typeof opts.flushMs === 'number' ? opts.flushMs : FLUSH_MS,
    flowControl: !!opts.flowControl,
  });
  if (opts.signal) {
    const onAbort = () => {
      try { worker.postMessage({ type: 'abort' }); } catch {}
      cleanup();
    };
    if (opts.signal.aborted) onAbort();
    else opts.signal.addEventListener('abort', onAbort, { once: true });
  }
  const controller: SqlStreamController & { ready?: () => void } = {
    abort: () => {
      try { worker.postMessage({ type: 'abort' }); } catch {}
      cleanup();
    },
    ready: () => {
      try { worker.postMessage({ type: 'ready' }); } catch {}
    },
  };
  return controller;
};


export const fetchSqlQueryStreaming = (
  opts: { query_string: string; batchSize?: number; flushMs?: number; headers?: Record<string, string>; onBatch?: (rows: any[]) => void; onDone?: () => void; onError?: (error: any) => void; signal?: AbortSignal; flowControl?: boolean; chunkSize?: number; statementTimeoutMs?: number }
): SqlStreamController & { ready?: () => void } => {
  const base = getApiBaseUrl();
  const url = `${base}/dbExchange/stream_sql_query`;
  const token = getAuthToken();
  const headers: Record<string, string> = {
    Accept: 'application/x-ndjson',
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(opts.headers || {}),
  };
  const worker = createNDJSONStreamWorker();
  const cleanup = () => {
    try { worker.terminate(); } catch {}
  };
  worker.onmessage = (evt: MessageEvent<any>) => {
    const msg = evt.data || {};
    if (msg.type === 'batch' && Array.isArray(msg.rows)) {
      opts.onBatch?.(msg.rows);
    } else if (msg.type === 'done') {
      opts.onDone?.();
      cleanup();
    } else if (msg.type === 'error') {
      opts.onError?.(msg.error || new Error('stream error'));
      cleanup();
    } else if (msg.type === 'aborted') {
      cleanup();
    }
  };
  worker.postMessage({
    type: 'start',
    url,
    headers,
    batchSize: typeof opts.batchSize === 'number' ? opts.batchSize : BATCH_SIZE,
    flushMs: typeof opts.flushMs === 'number' ? opts.flushMs : FLUSH_MS,
    flowControl: !!opts.flowControl,
    method: 'POST',
    body: { query_string: opts.query_string, chunk_size: opts.chunkSize, statement_timeout_ms: opts.statementTimeoutMs },
  });
  if (opts.signal) {
    const onAbort = () => {
      try { worker.postMessage({ type: 'abort' }); } catch {}
      cleanup();
    };
    if (opts.signal.aborted) onAbort();
    else opts.signal.addEventListener('abort', onAbort, { once: true });
  }
  const controller: SqlStreamController & { ready?: () => void } = {
    abort: () => {
      try { worker.postMessage({ type: 'abort' }); } catch {}
      cleanup();
    },
    ready: () => {
      try { worker.postMessage({ type: 'ready' }); } catch {}
    },
  };
  return controller;
};
